import type { ReactNode } from "react";
import { X } from "lucide-react";

export function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="card p-5 w-full max-w-md relative z-10">
        <header className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold">{title}</h3>
          <button
            type="button"
            className="text-muted hover:text-text"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}

/** Confirmation for a destructive action (revoke, remove). The action's
 *  error, if it fails, renders inside the dialog so it is not lost. */
export function ConfirmModal({
  title,
  message,
  confirmLabel,
  cancelLabel,
  busy = false,
  error,
  onConfirm,
  onClose,
}: {
  title: string;
  message: ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  busy?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Modal title={title} onClose={onClose}>
      <div className="space-y-4">
        <div className="text-sm">{message}</div>
        {error && (
          <div className="card p-3 text-sm text-bad border-bad/40 bg-bad/10">{error}</div>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn" onClick={onClose}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className="btn-primary !bg-bad !border-bad"
            disabled={busy}
            onClick={onConfirm}
          >
            {busy ? (
              <span
                className="inline-block animate-spin rounded-full border-2 border-current border-t-transparent"
                style={{ width: 14, height: 14 }}
                aria-label="loading"
              />
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
}
